import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, Loader2, BookOpen, Users, BarChart2, FileSpreadsheet, Printer } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { getApiUrl } from "../../config/api";
import { TERM_OPTIONS, ASSESSMENT_TYPES } from "../../constants/assessments";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

// ─── Types ────────────────────────────────────────────────────────────────────
interface ClassItem { id: string; name: string; }
interface SubjectItem { id: string; name: string; short_name?: string | null; }
interface StudentItem { id: string; name: string; classId: string; studentId?: string; admissionNumber?: string; }
interface ScoreEntry {
  studentId: string;
  subjectId: string;
  score: number | "";
  resultId?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString());

// ─── Helpers ──────────────────────────────────────────────────────────────────
const scoreColor = (score: number | "") => {
  if (score === "" || score === undefined) return "text-slate-400";
  if (score >= 80) return "text-emerald-600 font-bold";
  if (score >= 50) return "text-blue-600 font-semibold";
  return "text-red-500 font-semibold";
};

const scoreBg = (score: number | "") => {
  if (score === "" || score === undefined) return "bg-slate-50";
  if (score >= 80) return "bg-emerald-50";
  if (score >= 50) return "bg-blue-50";
  return "bg-red-50";
};

const formatAssessmentMetric = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "N/A";
  return `${Math.round(value)}`;
};

const abbreviateSubject = (name: string) => {
  const cleaned = name.trim().toUpperCase();
  if (cleaned === "CREATIVE AND TECHNOLOGY STUDIES") return "CTS";
  if (cleaned === "HOME ECONOMICS") return "HE";
  if (cleaned === "PHYSICAL EDUCATION") return "P.E.";
  if (cleaned === "SCIENCE PAPER 1") return "SP1";
  if (cleaned === "SCIENCE PAPER 2") return "SP2";
  return name;
};

const getSubjectLabel = (subject: SubjectItem) => {
  const shortName = typeof subject.short_name === "string" ? subject.short_name.trim() : "";
  return shortName || abbreviateSubject(subject.name);
};

// ─── Component ────────────────────────────────────────────────────────────────
const StudentsPerformance: React.FC = () => {
  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isReadOnly = !["teacher", "admin", "superadmin"].includes(role); // admins can record too

  // ── State ──────────────────────────────────────────────────────────────────
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [students, setStudents] = useState<StudentItem[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<StudentItem[]>([]);
  const [scores, setScores] = useState<ScoreEntry[]>([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedAssessmentType, setSelectedAssessmentType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Fetch classes ──────────────────────────────────────────────────────────
  const fetchClasses = useCallback(async () => {
    if (!token) return;
    try {
      let classesData: any[] = [];
      if (role === "teacher") {
        const res = await axios.get(getApiUrl("teacher/dashboard"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        classesData = res.data.classes || [];
      } else {
        const res = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        classesData = Array.isArray(res.data) ? res.data : res.data.classes || [];
      }
      setClasses(classesData.map((c: any) => ({ id: String(c.id || c._id), name: c.name })));
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  }, [token, role]);

  // ── Fetch subjects ─────────────────────────────────────────────────────────
  const fetchSubjects = useCallback(async () => {
    if (!selectedClass || !token) { setSubjects([]); return; }
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${selectedClass}/subjects`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data.subjects || [];
      setSubjects(data.map((s: any) => ({ id: String(s.id || s._id), name: s.name, short_name: s.short_name || null })));
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setSubjects([]);
    }
  }, [selectedClass, token]);

  // ── Fetch students ─────────────────────────────────────────────────────────
  const fetchStudents = useCallback(async () => {
    if (!selectedClass || !token) {
      setStudents([]); setFilteredStudents([]); setScores([]);
      return;
    }
    setLoading(true);
    try {
      const endpoint = role === "teacher"
        ? `teacher/classes/${selectedClass}/students`
        : `academics/classes/${selectedClass}/students`;

      const res = await axios.get(getApiUrl(endpoint), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data.students || [];
      const mapped: StudentItem[] = data.map((s: any) => ({
        id: String(s.id || s._id),
        name: s.name || s.fullName || "Unknown",
        classId: selectedClass,
        studentId: s.studentId,
      }));
      setStudents(mapped);
      setFilteredStudents(mapped);
      // Initialize score grid
      if (subjects.length > 0) {
        const initialScores: ScoreEntry[] = [];
        mapped.forEach(stu => {
          subjects.forEach(sub => {
            initialScores.push({ studentId: stu.id, subjectId: sub.id, score: "" });
          });
        });
        setScores(initialScores);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load students.");
      setStudents([]); setFilteredStudents([]); setScores([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, subjects, token, role]);

  // ── Fetch existing results ─────────────────────────────────────────────────
  const fetchExistingResults = useCallback(async () => {
    if (!selectedClass || !selectedTerm || !selectedAssessmentType || !selectedYear || !token) return;
    setLoadingResults(true);
    try {
      const url = `assessments/results/class/${selectedClass}?examType=${encodeURIComponent(selectedAssessmentType)}&term=${encodeURIComponent(selectedTerm)}&year=${encodeURIComponent(selectedYear)}`;
      const res = await axios.get(getApiUrl(url), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const results = Array.isArray(res.data) ? res.data : [];

      // Build a lookup map
      const map = new Map<string, { id: string; score: number }>();
      results.forEach((r: any) => {
        const sid = String(r.studentId || "");
        const bid = String(r.subjectId || "");
        if (sid && bid) {
          map.set(`${sid}-${bid}`, { id: String(r.id), score: Number(r.score) });
        }
      });

      // Merge into score grid
      setScores(prev => {
        if (prev.length === 0) return prev;
        return prev.map(s => {
          const match = map.get(`${s.studentId}-${s.subjectId}`);
          return match
            ? { ...s, score: match.score, resultId: match.id }
            : { ...s, score: "", resultId: undefined };
        });
      });
    } catch (err) {
      console.error("Error fetching results:", err);
    } finally {
      setLoadingResults(false);
    }
  }, [selectedClass, selectedTerm, selectedAssessmentType, selectedYear, token]);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchClasses(); }, [fetchClasses]);

  useEffect(() => {
    if (selectedClass) fetchSubjects();
    else setSubjects([]);
  }, [selectedClass, fetchSubjects]);

  useEffect(() => {
    if (selectedClass && subjects.length > 0) fetchStudents();
  }, [selectedClass, subjects.length, fetchStudents]);

  useEffect(() => {
    if (selectedClass && selectedTerm && selectedAssessmentType && students.length > 0 && scores.length > 0) {
      fetchExistingResults();
    }
  }, [selectedClass, selectedTerm, selectedAssessmentType, selectedYear, students.length, scores.length, fetchExistingResults]);

  // Search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
    } else {
      setFilteredStudents(
        students.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
  }, [searchTerm, students]);

  // Reset when class changes
  useEffect(() => {
    setSelectedAssessmentType("");
    setSelectedTerm("");
    setSearchTerm("");
    setScores([]);
  }, [selectedClass]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtersReady = !!selectedClass && !!selectedTerm && !!selectedAssessmentType && !!selectedYear;

  const getScore = (studentId: string, subjectId: string): number | "" => {
    const entry = scores.find(s => s.studentId === studentId && s.subjectId === subjectId);
    return entry?.score ?? "";
  };

  const getAvg = (studentId: string): number | null => {
    const studentScores = scores
      .filter(s => s.studentId === studentId && s.score !== "")
      .map(s => Number(s.score));
    if (studentScores.length === 0) return null;
    return studentScores.reduce((a, b) => a + b, 0) / studentScores.length;
  };

  const getTotal = (studentId: string): number | null => {
    const studentScores = scores
      .filter(s => s.studentId === studentId && s.score !== "")
      .map(s => Number(s.score));
    if (studentScores.length === 0) return null;
    return studentScores.reduce((a, b) => a + b, 0);
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExportExcel = () => {
    if (!filtersReady || filteredStudents.length === 0) return;
    const className = classes.find(c => c.id === selectedClass)?.name || "N/A";
    const wb = XLSX.utils.book_new();

    const headerRow = ["#", "Student Name", "Student ID", ...subjects.map(s => getSubjectLabel(s)), "Total", "Average", "Status"];
    const rows: any[][] = [
      [`MILESTONE SCHOOL — STUDENT PERFORMANCE REPORT`],
      [`Class: ${className} | Term: ${selectedTerm} | Assessment: ${selectedAssessmentType} | Year: ${selectedYear}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      headerRow,
    ];

    filteredStudents.forEach((stu, idx) => {
      const subjectScores = subjects.map(sub => {
        const sc = getScore(stu.id, sub.id);
        return sc === "" ? "" : sc;
      });
      const total = getTotal(stu.id);
      const avg = getAvg(stu.id);
      rows.push([
        idx + 1,
        stu.name,
        stu.admissionNumber || stu.studentId || String(stu.id).slice(-6).toUpperCase(),
        ...subjectScores,
        formatAssessmentMetric(total),
        formatAssessmentMetric(avg),
        avg !== null ? (avg >= 50 ? "PASS" : "FAIL") : "N/A",
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 5 }, { wch: 28 }, { wch: 14 }, ...subjects.map(() => ({ wch: 14 })), { wch: 10 }, { wch: 10 }, { wch: 8 }];
    XLSX.utils.book_append_sheet(wb, ws, "Performance");
    XLSX.writeFile(wb, `performance_${className}_${selectedAssessmentType}_${selectedYear}.xlsx`);
  };

  const handlePrint = () => {
    if (!filtersReady) return;
    window.print();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="utopia-page">
      {/* Page Header */}
      <div className="utopia-page-header">
        <div>
          <h1 className="utopia-page-title">Score Sheets</h1>
          <p className="page-subtitle">
            {isReadOnly
              ? "View assessment results across classes and subjects"
              : "Record and review student assessment scores"}
          </p>
        </div>
        {filtersReady && filteredStudents.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              className="utopia-btn utopia-btn-secondary flex items-center gap-2"
            >
              <FileSpreadsheet size={16} />
              Export Excel
            </button>
            <button
              onClick={handlePrint}
              className="utopia-btn utopia-btn-secondary flex items-center gap-2"
            >
              <Printer size={16} />
              Print
            </button>
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="utopia-card mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {/* Class */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Class *</label>
            <select
              className="utopia-input"
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
            >
              <option value="">Select Class...</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Year *</label>
            <select
              className="utopia-input"
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
            >
              {YEARS.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Term */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Term *</label>
            <select
              className="utopia-input"
              value={selectedTerm}
              onChange={e => setSelectedTerm(e.target.value)}
            >
              <option value="">Select Term...</option>
              {TERM_OPTIONS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Assessment Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Assessment Type *</label>
            <select
              className="utopia-input"
              value={selectedAssessmentType}
              onChange={e => setSelectedAssessmentType(e.target.value)}
            >
              <option value="">Select Assessment Type...</option>
              {ASSESSMENT_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {!selectedClass ? (
        <div className="utopia-card flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
            <BookOpen className="text-indigo-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-1">Select a class to get started</h3>
          <p className="text-slate-500 text-sm">Choose a class, year, term, and assessment type to view performance data.</p>
        </div>
      ) : loading ? (
        <div className="utopia-card flex flex-col items-center justify-center py-20">
          <Loader2 className="animate-spin text-indigo-500 mb-3" size={36} />
          <p className="text-slate-500 font-medium">Loading students…</p>
        </div>
      ) : students.length === 0 ? (
        <div className="utopia-card flex flex-col items-center justify-center py-20 text-center">
          <Users className="text-slate-300 mb-4" size={48} />
          <p className="text-slate-500 font-medium">No students found in this class.</p>
        </div>
      ) : !selectedAssessmentType ? (
        <div className="utopia-card flex flex-col items-center justify-center py-16 text-center">
          <BarChart2 className="text-slate-300 mb-4" size={48} />
          <p className="text-slate-600 font-semibold">
            {students.length} student{students.length !== 1 ? "s" : ""} loaded
          </p>
          <p className="text-slate-400 text-sm mt-1">Select an Assessment Type to view scores.</p>
        </div>
      ) : (
        <div className="utopia-card p-0 overflow-hidden">
          {/* Table top bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-700">
                {filteredStudents.length} student{filteredStudents.length !== 1 ? "s" : ""}
              </span>
              {loadingResults && (
                <span className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                  <Loader2 className="animate-spin" size={12} />
                  Loading scores…
                </span>
              )}
              {isReadOnly && (
                <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                  View Only
                </span>
              )}
            </div>
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <input
                type="text"
                placeholder="Search students…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="utopia-search-input pl-9 h-9 text-sm"
              />
            </div>
          </div>

          {/* Scrollable score grid */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 bg-slate-50 text-left py-3 px-4 font-semibold text-slate-600 whitespace-nowrap z-10 border-r border-slate-200">
                    #
                  </th>
                  <th className="sticky left-8 bg-slate-50 text-left py-3 px-4 font-semibold text-slate-600 whitespace-nowrap z-10 min-w-[180px] border-r border-slate-200">
                    Student
                  </th>
                  {subjects.map(sub => (
                    <th
                      key={sub.id}
                      className="py-3 px-4 font-semibold text-slate-600 text-center whitespace-nowrap min-w-[110px]"
                    >
                      {getSubjectLabel(sub)}
                    </th>
                  ))}
                  <th className="py-3 px-4 font-semibold text-slate-700 text-center whitespace-nowrap bg-slate-100">
                    Total
                  </th>
                  <th className="py-3 px-4 font-semibold text-slate-700 text-center whitespace-nowrap bg-slate-100">
                    Average
                  </th>
                  <th className="py-3 px-4 font-semibold text-slate-700 text-center whitespace-nowrap bg-slate-100">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={subjects.length + 5} className="py-12 text-center text-slate-400">
                      No students match your search.
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((stu, idx) => {
                    const total = getTotal(stu.id);
                    const avg = getAvg(stu.id);
                    const isPassing = avg !== null && avg >= 50;

                    return (
                      <tr key={stu.id} className="hover:bg-slate-50/60 transition-colors">
                        {/* Row number */}
                        <td className="sticky left-0 bg-white py-3 px-4 text-slate-400 font-medium text-xs border-r border-slate-100 z-10">
                          {idx + 1}
                        </td>
                        {/* Student name */}
                        <td className="sticky left-8 bg-white py-3 px-4 border-r border-slate-100 z-10">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {stu.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800 leading-tight">{stu.name}</p>
                              {stu.studentId && (
                                <p className="text-xs text-slate-400">{stu.studentId}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Subject score cells — read-only display */}
                        {subjects.map(sub => {
                          const score = getScore(stu.id, sub.id);
                          return (
                            <td key={sub.id} className={`py-2 px-3 text-center ${scoreBg(score)}`}>
                              {score === "" ? (
                                <span className="text-slate-300 text-xs">—</span>
                              ) : (
                                <span className={`text-sm ${scoreColor(score)}`}>
                                  {score}
                                </span>
                              )}
                            </td>
                          );
                        })}

                        {/* Average */}
                        <td className="py-2 px-4 text-center bg-slate-50/80">
                          {total !== null ? (
                            <span className={`text-sm font-bold ${scoreColor(total)}`}>
                              {formatAssessmentMetric(total)}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>

                        {/* Average */}
                        <td className="py-2 px-4 text-center bg-slate-50/80">
                          {avg !== null ? (
                            <span className={`text-sm font-bold ${scoreColor(avg)}`}>
                              {formatAssessmentMetric(avg)}%
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>

                        {/* Pass/Fail badge */}
                        <td className="py-2 px-4 text-center bg-slate-50/80">
                          {avg !== null ? (
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold ${isPassing
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-700"
                              }`}>
                              {isPassing ? "PASS" : "FAIL"}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPerformance;
